import React, { useState } from "react";
import ReactDOM from "react-dom";
import { Badge, Image, Button, Form } from "react-bootstrap";
import { X, ThumbsUp, Send, Share2, ZoomIn } from "lucide-react";
import { FacebookCounter, FacebookSelector } from "@charkour/react-reactions";
import CommentsSection from "../comment-section/CommentsSection";
import "./ArticleDetailModal.scss";
import { setPostReaction } from "../../data/communityData";
import { linkifyText, sanitizeRichContent } from "../../utils/communityHelpers";
import parse from "html-react-parser";
import ImageZoomModal from "../image-zoom-modal";
import UserProfileModal from "../user-profile-modal/UserProfileModal";
import { getUser } from "../../../../utils/auth";
import Linkify from "linkify-react";

import MessageCircle from "../../../../assets/images/messagecircle.svg";

const ArticleDetailModal = ({ show, post, onClose, onShare }) => {
  // mirror PostCard local state/handlers for like/reactions
  const [currentPost, setCurrentPost] = useState(null);
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [showImageZoom, setShowImageZoom] = useState(false);
  const [hoverHideTimer, setHoverHideTimer] = useState(null);

  // User Profile Modal state
  const [showUserProfileModal, setShowUserProfileModal] = useState(false);

  const currentUser = getUser();
  const isOwnPost = currentPost?._metadata?.userId === currentUser?.id;

  // Update currentPost when post prop changes
  React.useEffect(() => {
    if (post) {
      setCurrentPost(post);
    }
  }, [post]);

  if (!show || !post || !currentPost) return null;

  const handleLike = () => {
    if (currentPost.userReaction) {
      const updatedPost = setPostReaction(
        currentPost.id,
        currentPost.userReaction,
      );
      if (updatedPost) {
        setCurrentPost({ ...updatedPost });
      }
      return;
    }
  };

  const handleReactionsEnter = () => {
    if (hoverHideTimer) clearTimeout(hoverHideTimer);
    setShowReactionPicker(true);
  };
  const handleReactionsLeave = () => {
    const t = setTimeout(() => setShowReactionPicker(false), 250);
    setHoverHideTimer(t);
  };
  const handleReactionPick = (reaction) => {
    const updatedPost = setPostReaction(currentPost.id, reaction);
    if (updatedPost) {
      setCurrentPost({ ...updatedPost });
    }
    setShowReactionPicker(false);
  };

  /**
   * Handle user profile click
   */
  const handleUserProfileClick = (e) => {
    e.stopPropagation();
    
    // Check for user ID in different possible locations
    const userId = currentPost?._metadata?.userId || currentPost?.userId || currentPost?.user_id;
    
    // Don't open modal for anonymous posts or own posts
    if (currentPost?._metadata?.isAnonymous === 1 || isOwnPost) {
      return;
    }
    
    // Don't open if no user ID available
    if (!userId) {
      return;
    }
    
    setShowUserProfileModal(true);
  };

  return (
    <div className="article-modal-backdrop" onClick={onClose}>
      <div className="article-modal" onClick={(e) => e.stopPropagation()}>
        <Button
          variant="link"
          className="icon-btn"
          onClick={onClose}
          aria-label="Close"
        >
          <X size={18} />
        </Button>
        <div className="article-modal-content">
          {/* Header */}
          <div className="article-modal-header">
            <div className="author-block">
              <Image
                src={currentPost.authorImage}
                alt={currentPost.author}
                roundedCircle
                width={40}
                height={40}
                className="me-3"
                style={{
                  cursor:
                    currentPost?._metadata?.isAnonymous !== 1 && !isOwnPost
                      ? "pointer"
                      : "default",
                }}
                onClick={handleUserProfileClick}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                    currentPost.author || "User",
                  )}&background=0195D3&color=fff&size=40`;
                }}
              />
              <div className="author-meta">
                <div
                  className="author-name"
                  style={{
                    cursor:
                      currentPost?._metadata?.isAnonymous !== 1 && !isOwnPost
                        ? "pointer"
                        : "default",
                  }}
                  onClick={handleUserProfileClick}
                >
                  {currentPost.author}
                </div>
                <div className="post-time">{currentPost.time}</div>
              </div>
            </div>
            <div className="header-actions">
              {currentPost.categoryTag && (
                <Badge bg="light" text="dark" className="category-pill">
                  {currentPost.categoryTag}
                </Badge>
              )}
            </div>
          </div>

          {/* Body */}
          <div className="article-modal-body">
            <div className="article-content">
              {(() => {
                const hasHtml =
                  typeof currentPost.content === "string" &&
                  /<\/?[a-z][\s\S]*>/i.test(currentPost.content);
                return hasHtml ? (
                  <Linkify>
                    <div className="rich-content">
                      {parse(sanitizeRichContent(currentPost.content))}
                    </div>
                  </Linkify>
                ) : (
                  linkifyText(currentPost.content || "").map((part, index) =>
                    part.type === "link" ? (
                      <a
                        key={index}
                        href={part.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#3b82f6",
                          textDecoration: "underline",
                          wordBreak: "break-all",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {part.content}
                      </a>
                    ) : (
                      <span key={index}>{part.content}</span>
                    ),
                  )
                );
              })()}
            </div>

            {currentPost.image && (
              <div className="article-image-wrap">
                <button
                  className="image-zoom-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowImageZoom(true);
                  }}
                  title="Click to zoom"
                  // style={{
                  //   position: 'absolute',
                  //   top: '12px',
                  //   right: '12px',
                  //   zIndex: 1000,
                  //   background: 'rgba(0, 0, 0, 0.8)',
                  //   border: '2px solid white',
                  //   color: 'white',
                  //   padding: '10px',
                  //   borderRadius: '8px',
                  //   cursor: 'pointer',
                  //   display: 'flex',
                  //   alignItems: 'center',
                  //   justifyContent: 'center'
                  // }}
                >
                  <ZoomIn size={20} />
                </button>
                <Image
                  src={currentPost.image}
                  alt="Post"
                  fluid
                  className="article-image"
                  onError={(e) => {
                    e.target.onerror = null;
                    // Use base64 encoded SVG placeholder
                    e.target.src =
                      "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNjAwIiBoZWlnaHQ9IjQwMCIgZmlsbD0iI2U1ZTdlYiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM2Yjcy ODAlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkb21pbmFudC1iYXNlbGluZT0ibWlkZGxlIj5JbWFnZSBOb3QgQXZhaWxhYmxlPC90ZXh0Pjwvc3ZnPg==";
                  }}
                />
              </div>
            )}

            {/* Reaction Summary - HYBRID: Shows both web reactions + legacy likes */}
            <div className="py-2 border-bottom">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center reaction-container">
                  <FacebookCounter
                    counters={(() => {
                      const order = [
                        "like",
                        "love",
                        "haha",
                        "wow",
                        "sad",
                        "angry",
                      ];
                      const list = [];
                      const counts = currentPost.reactions || {};

                      // Calculate how many legacy likes exist (from mobile)
                      const currentReactionTotal = Object.values(counts).reduce(
                        (sum, count) => sum + count,
                        0,
                      );
                      const legacyLikesCount = Math.max(
                        0,
                        currentPost.likes - currentReactionTotal,
                      );

                      // Add legacy likes as 'like' reactions
                      for (let i = 0; i < legacyLikesCount; i++) {
                        list.push({ emoji: "like", by: `legacy-like-${i}` });
                      }

                      // Add web reactions
                      order.forEach((type) => {
                        const n = Math.max(0, counts[type] || 0);
                        for (let i = 0; i < n; i++) {
                          if (
                            currentPost.userReaction === type &&
                            !list.some(
                              (e) => e.by === "You" && e.emoji === type,
                            )
                          ) {
                            list.push({ emoji: type, by: "You" });
                          } else {
                            list.push({ emoji: type, by: `user-${type}-${i}` });
                          }
                        }
                      });
                      return list;
                    })()}
                    user={"You"}
                    bg={"#fff"}
                    alwaysShowOthers={true}
                  />
                  {/* <span className="ms-2" style={{ fontSize: '14px', color: '#65676b' }}>{currentPost.likes}</span> */}
                </div>
                <div className="d-flex align-items-center comments-counter">
                  {currentPost.comments > 0 && (
                    <span>{currentPost.comments} comments</span>
                  )}
                  {currentPost.shares > 0 && (
                    <span>{currentPost.shares} shares</span>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons Row (exactly like PostCard) */}
            <div className="d-flex align-items-center justify-content-between py-2">
              <div
                className="d-flex align-items-center gap-3"
                style={{ fontSize: "14px", fontWeight: 500 }}
              >
                <div
                  className="position-relative"
                  onMouseEnter={handleReactionsEnter}
                  onMouseLeave={handleReactionsLeave}
                >
                  <Button
                    variant="link"
                    size="sm"
                    onClick={handleLike}
                    className={`p-0 d-flex align-items-center gap-2 action-btn ${
                      currentPost.userReaction ? "text-primary" : "text-muted"
                    }`}
                    style={{
                      fontSize: "14px",
                      fontWeight: "500",
                      textDecoration: "none",
                    }}
                  >
                    <ThumbsUp
                      size={22}
                      fill={currentPost.userReaction ? "currentColor" : "none"}
                    />
                    <span>Like</span>
                  </Button>

                  {showReactionPicker && (
                    <div
                      className="reaction-picker-popup"
                      style={{
                        position: "absolute",
                        bottom: "100%",
                        left: 16,
                        zIndex: 1000,
                        marginBottom: 8,
                      }}
                      onMouseEnter={handleReactionsEnter}
                      onMouseLeave={handleReactionsLeave}
                    >
                      <FacebookSelector onSelect={handleReactionPick} />
                    </div>
                  )}
                </div>

                <Button
                  variant="link"
                  size="sm"
                  className="p-0 d-flex align-items-center gap-2 action-btn text-muted"
                  style={{
                    fontSize: "14px",
                    fontWeight: "500",
                    textDecoration: "none",
                  }}
                >
                  {/* <MessageCircle size={16} /> */}
                  <img src={MessageCircle} width={22} height={22} />
                  <span>Comment</span>
                </Button>

                <Button
                  variant="link"
                  size="sm"
                  onClick={() => onShare?.(currentPost)}
                  className="p-0 d-flex align-items-center gap-2 action-btn text-muted"
                  style={{
                    fontSize: "14px",
                    fontWeight: "500",
                    textDecoration: "none",
                  }}
                >
                  <Send size={22} />
                  <span>Share</span>
                </Button>
              </div>
            </div>

            {/* Comments thread */}
            <CommentsSection postId={currentPost.id} />
          </div>

          {/* Footer composer */}
          {/* <div className="article-modal-footer">
          <Image
            src="https://api.dicebear.com/7.x/avataaars/svg?seed=CurrentUser&backgroundColor=a7f3d0&clothingColor=1f2937"
            alt="You"
            roundedCircle
            width={36}
            height={36}
          />
          <Form.Control className="comment-input" placeholder="Write comments" />
          <div className="footer-icons">
            <span className="footer-icon" role="img" aria-label="image">🖼️</span>
            <span className="footer-icon" role="img" aria-label="send">📨</span>
          </div>
        </div> */}
        </div>
      </div>

      {/* Image Zoom Modal */}
      <ImageZoomModal
        show={showImageZoom}
        onClose={() => setShowImageZoom(false)}
        imageUrl={currentPost?.image}
        images={currentPost?.post_images?.map(
          (img) =>
            `${process.env.REACT_APP_API_BASE_URL}/uploads/post_images/${img.image_path}`,
        )}
      />

      {/* User Profile Modal - Rendered via Portal */}
      {showUserProfileModal &&
        ReactDOM.createPortal(
          <UserProfileModal
            show={showUserProfileModal}
            onHide={() => setShowUserProfileModal(false)}
            userId={
              currentPost?._metadata?.userId ||
              currentPost?.userId ||
              currentPost?.user_id
            }
            userName={currentPost?.author}
            userImage={currentPost?.authorImage}
            userState={
              currentPost?._metadata?.userState ||
              currentPost?.userState ||
              currentPost?.user_state
            }
          />,
          document.body,
        )}
    </div>
  );
};

export default ArticleDetailModal;
